// =============================================================================
// ui/tabs/CoachVerdict.tsx — the AI Coach verdict.
// -----------------------------------------------------------------------------
// ── 🚨 NO TEMPLATED FALLBACK, AND THAT IS A DIVERGENCE ──
//
// The old client renders a TEMPLATED verdict first and swaps a live one in when
// the model answers, distinguished only by a small "· live" marker. So when the
// key is missing, the entitlement is off or the cap is spent, the card silently
// keeps a card headed "AI Coach verdict" that no model ever saw.
//
// This client shows nothing rather than something that reads as a verdict. It is
// the same rule the leak rows follow: three different silences, each said in its
// own words — no key, no entitlement, allowance spent, or simply too few trades.
// A card that goes quiet for four reasons and explains none is where a trader
// decides the feature is broken.
//
// ⚠ THE TEMPLATE IS ALSO NOT PORTABLE YET. It is built from risk of ruin, the
// E-ratio and the pattern × exit pairings — the four dimensions, which this
// client cannot measure. A template drawn from a subset would be a weaker claim
// wearing the same words.
//
// ── ⚠ THE MODEL'S TEXT IS RENDERED AS ELEMENTS, NEVER AS MARKUP ──
//
// `verdictSpans` splits the `**emphasis**` into data. The old client escapes and
// then sets `innerHTML`, which is safe only while the escape is right — on a
// page holding the trader's session, for a string that came from a model. Here
// there is no markup path to get wrong.
// =============================================================================

import { money, num } from '../format';
import { verdictSpans, type VerdictGate } from '../../selectors/aiVerdict';
import type { AiVerdictState } from '../state/useAiVerdict';

/** Model text with its emphasis, as elements. */
function Emphasised({ text }: { text: string }) {
  return (
    <>
      {verdictSpans(text).map((s, i) => (
        s.bold ? <b key={i}>{s.text}</b> : <span key={i}>{s.text}</span>
      ))}
    </>
  );
}

export function CoachVerdict(
  { gate, state, accountName, tradeCount, netPnl, onSeePlans }: {
    gate: VerdictGate;
    state: AiVerdictState;
    accountName: string | null;
    tradeCount: number;
    netPnl: number;
    /**
     * Where a Pro refusal sends the reader (#76).
     *
     * ⚠ ONLY for `why === 'pro'`. The other two silences — no account picked,
     * too few trades — are things the reader can fix themselves in a second, and
     * a "See plans" button under either would be selling them a subscription to
     * solve a problem a click already solves.
     */
    onSeePlans?: (() => void) | undefined;
  },
) {
  return (
    <section className="card coach-ai" data-testid="coach-verdict">
      <div className="coach-ai-head">
        <div className="coach-ai-tag">
          <span className="coach-ai-dot" aria-hidden="true" />
          AI Coach verdict
          {/* ⚠ The LIVE marker is only ever shown beside real model output.
              Nothing else in this card may borrow it. */}
          {state.phase === 'ready' ? <span className="coach-ai-live"> · live</span> : null}
        </div>
        {/* ⚠ Regenerate BUYS A RUN, so it hides with the gate — including the
            Pro one. A locked card with a live spend button on it would be the
            gate leaking through its own refusal. */}
        {gate.ok && state.phase !== 'loading' ? (
          <button type="button" className="btn coach-ai-regen"
            onClick={state.regenerate}>↻ Regenerate</button>
        ) : null}
      </div>

      {!gate.ok ? (
        <>
          {/* ⚠ SAYS WHICH silence this is, and how to leave it. */}
          <p className="muted coach-ai-idle" data-why={gate.why}>{gate.message}</p>
          {/* 🚨 THE REFUSAL HAS TO SELL, NOT JUST REFUSE (#76). This card is the
              one place a free user meets the AI Coach, and beta answers the same
              moment with a branded teaser and a price. A silent grey paragraph
              is a worse outcome than the ungated version it replaces: the reader
              learns the feature exists and is given nowhere to go. */}
          {gate.why === 'pro' && onSeePlans ? (
            <button type="button" className="btn primary coach-ai-cta"
              onClick={onSeePlans}>See plans</button>
          ) : null}
        </>
      ) : state.phase === 'loading' || state.phase === 'idle' ? (
        // ⚠ `idle` IS DRAWN, and it is not decoration. The gate is open, so a
        // request is coming — but there is a render between the gate opening and
        // the effect firing, and any state the card does not name renders as an
        // EMPTY card under a heading. Found on Josh's book: the whole section was
        // a title and a Regenerate button with nothing between them.
        <p className="muted coach-ai-idle">
          Reading {accountName ?? 'this account'} — {num(tradeCount, 0)} trade
          {tradeCount === 1 ? '' : 's'}, {money(netPnl)}…
        </p>
      ) : state.phase === 'failed' ? (
        <ExplainFailure state={state} />
      ) : state.verdict ? (
        <>
          <p className="coach-ai-verdict">
            <Emphasised text={state.verdict.verdict} />
          </p>
          <div className="coach-ai-grid">
            <div>
              <div className="muted coach-k">Fix first</div>
              {state.verdict.fixFirst.length ? state.verdict.fixFirst.map((a, i) => (
                <div className="coach-ai-act" key={i}>
                  <span className="coach-ai-n">{i + 1}</span>
                  <span><Emphasised text={a} /></span>
                </div>
              )) : (
                <p className="muted coach-ai-idle">
                  The coach returned no actions for this read.
                </p>
              )}
            </div>
            <div>
              <div className="muted coach-k">Watch</div>
              {state.verdict.watch ? (
                <p className="coach-ai-watch"><Emphasised text={state.verdict.watch} /></p>
              ) : (
                <p className="muted coach-ai-idle">Nothing flagged to watch.</p>
              )}
            </div>
          </div>
          {/* ⚠ SAID EVERY TIME, not once in a help drawer. The verdict is prose
              from a model, it is confident by construction, and the numbers
              above it are the ones the trader can check. */}
          <p className="muted coach-ai-foot">
            Written by a model from the figures on this page. It can be wrong —
            the numbers below are the ones to check it against.
          </p>
        </>
      ) : null}
    </section>
  );
}

/**
 * ⚠ FOUR OUTCOMES, AND THREE OF THEM ARE NORMAL. The old client swallows the
 * first two into a silent fallback; a trader on this client would otherwise have
 * no way to tell "coaching is switched off for you" from "the request failed".
 */
function ExplainFailure({ state }: { state: AiVerdictState }) {
  if (state.code === 'AI_NOT_CONFIGURED') {
    return (
      <p className="muted coach-ai-idle" data-why="not-configured">
        AI coaching isn’t switched on for this deployment yet. Everything below is
        measured locally and is unaffected.
      </p>
    );
  }
  if (state.code === 'AI_NOT_ENABLED') {
    return (
      <p className="muted coach-ai-idle" data-why="not-enabled">
        AI coaching isn’t enabled on your account. Everything below is measured
        locally and is unaffected.
      </p>
    );
  }
  if (state.code === 'COACH_CAP') {
    return (
      <p className="muted coach-ai-idle" data-why="cap">
        You’ve used your coaching allowance for now — it resets daily. Nothing
        below depends on it.
      </p>
    );
  }
  return (
    <div className="coach-ai-idle" role="alert" data-why="failed">
      <p className="muted">The coach couldn’t be reached. {state.error}</p>
      <button type="button" className="btn" onClick={state.regenerate}>Try again</button>
    </div>
  );
}
